import React, { useEffect, useState } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import {
  CheckIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import api from "../services/api";
import TagBadge from "./TagBadge";

export default function TagManagerDialog({ open, onClose, tags, loading, onChanged }) {
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setNewName("");
      setEditingId(null);
      setEditingName("");
      setError("");
    }
  }, [open]);

  const createTag = async (event) => {
    event.preventDefault();
    const name = newName.trim();
    if (!name) return;
    try {
      setBusy(true);
      setError("");
      await api.post("/stockage/tags", { Nom: name });
      setNewName("");
      await onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Impossible de créer le tag.");
    } finally {
      setBusy(false);
    }
  };

  const updateTag = async (tagId) => {
    const name = editingName.trim();
    if (!name) return;
    try {
      setBusy(true);
      setError("");
      await api.put(`/stockage/tags/${tagId}`, { Nom: name });
      setEditingId(null);
      setEditingName("");
      await onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Impossible de modifier le tag.");
    } finally {
      setBusy(false);
    }
  };

  const deleteTag = async (tag) => {
    if (!window.confirm(`Supprimer le tag « ${tag.Nom} » de tous tes fichiers ?`)) return;
    try {
      setBusy(true);
      setError("");
      await api.delete(`/stockage/tags/${tag.TagID}`);
      if (editingId === tag.TagID) setEditingId(null);
      await onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Impossible de supprimer le tag.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? () => {} : onClose} className="relative z-50">
      <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm" aria-hidden="true" />
      <div className="fixed inset-0 overflow-y-auto p-4 sm:flex sm:items-center sm:justify-center">
        <DialogPanel className="mx-auto w-full max-w-xl overflow-hidden rounded-2xl border border-sky-500/10 bg-white shadow-2xl shadow-slate-950/20 dark:bg-slate-950">
          <div className="flex items-start justify-between gap-4 border-b border-sky-500/10 bg-gradient-to-r from-sky-500/15 via-blue-500/10 to-transparent px-6 py-5">
            <div>
              <DialogTitle className="text-xl font-black text-slate-950 dark:text-white">Gérer mes tags</DialogTitle>
              <p className="mt-1 text-sm font-semibold text-slate-500 dark:text-slate-400">
                Chaque tag reçoit automatiquement une couleur Tailwind différente.
              </p>
            </div>
            <button type="button" onClick={onClose} disabled={busy} className="inline-flex size-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-500/10 dark:text-slate-300" title="Fermer">
              <XMarkIcon className="size-5" />
            </button>
          </div>

          <div className="grid gap-5 p-6">
            <form onSubmit={createTag} className="flex flex-col gap-3 sm:flex-row">
              <input
                type="text"
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                maxLength={50}
                placeholder="Nouveau tag"
                className="min-w-0 flex-1 rounded-xl border border-sky-500/20 bg-white px-4 py-3 text-sm font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-sky-400 dark:bg-slate-950/70 dark:text-white"
              />
              <button type="submit" disabled={busy || !newName.trim()} className="inline-flex items-center justify-center gap-2 rounded-xl bg-sky-500 px-5 py-3 text-sm font-bold text-white hover:bg-sky-600 disabled:cursor-not-allowed disabled:opacity-60">
                <PlusIcon className="size-5" /> Ajouter
              </button>
            </form>

            {error && <p className="text-sm font-semibold text-red-600 dark:text-red-300">{error}</p>}

            <div className="grid gap-2">
              {loading ? (
                <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">Chargement...</p>
              ) : tags.length === 0 ? (
                <p className="rounded-xl border border-dashed border-slate-300 p-4 text-sm font-semibold text-slate-500 dark:border-slate-700 dark:text-slate-400">Tu n’as pas encore créé de tag.</p>
              ) : tags.map((tag) => (
                <div key={tag.TagID} className="flex items-center gap-3 rounded-xl border border-slate-200/70 p-3 dark:border-slate-800">
                  {editingId === tag.TagID ? (
                    <input
                      type="text"
                      value={editingName}
                      onChange={(event) => setEditingName(event.target.value)}
                      maxLength={50}
                      className="min-w-0 flex-1 rounded-lg border border-sky-500/20 bg-white px-3 py-2 text-sm font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-sky-400 dark:bg-slate-900 dark:text-white"
                      autoFocus
                    />
                  ) : (
                    <div className="min-w-0 flex-1"><TagBadge tag={tag} /></div>
                  )}
                  {editingId === tag.TagID ? (
                    <>
                      <button type="button" onClick={() => updateTag(tag.TagID)} disabled={busy || !editingName.trim()} className="inline-flex size-9 items-center justify-center rounded-lg text-emerald-700 hover:bg-emerald-500/10 disabled:opacity-50 dark:text-emerald-300" title="Enregistrer"><CheckIcon className="size-5" /></button>
                      <button type="button" onClick={() => setEditingId(null)} disabled={busy} className="inline-flex size-9 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-500/10 dark:text-slate-300" title="Annuler"><XMarkIcon className="size-5" /></button>
                    </>
                  ) : (
                    <button type="button" onClick={() => { setEditingId(tag.TagID); setEditingName(tag.Nom); setError(""); }} disabled={busy} className="inline-flex size-9 items-center justify-center rounded-lg text-sky-700 hover:bg-sky-500/10 dark:text-sky-300" title="Renommer"><PencilSquareIcon className="size-5" /></button>
                  )}
                  <button type="button" onClick={() => deleteTag(tag)} disabled={busy} className="inline-flex size-9 items-center justify-center rounded-lg text-red-700 hover:bg-red-500/10 disabled:opacity-50 dark:text-red-300" title="Supprimer"><TrashIcon className="size-5" /></button>
                </div>
              ))}
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
